import { useEffect, useRef, useState } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from "@mui/material";
import WindowIcon from "@mui/icons-material/Window";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { tokens } from "../../global/theme/tokens";
import Header from "../../components/special/Header";
import "react-circular-progressbar/dist/styles.css";
import { API_GET, DBRequest, useConfig } from "../../api/api";
import Skeleton from "@mui/material/Skeleton";
import ProjectTimeCard from "./ProjectTimeCard";
import TimeDashboard from "./TimeDashboard";
import { useTranslation } from "react-i18next";
import { TimeAnimation } from "../../components/animation/TimeAnimation";
import { useTimeTracking } from "../../components/time-tracking/AppTimeProvider";
import { ProjectList } from "./project-list/ProjectList";
import { SearchField } from "../../components/exo/search/SearchField";

const VIEW_STATE_KEY = "time-tracking-view-state";

function readViewState() {
  try {
    return JSON.parse(localStorage.getItem(VIEW_STATE_KEY) || "{}");
  } catch {
    return {};
  }
}

const TimeTracking = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  // view options ----------------------------------------
  const [viewMode, setViewMode] = useState(
    () => readViewState().viewMode || "grid"
  );
  const [showAll, setShowAll] = useState(() =>
    Boolean(readViewState().showAll)
  );
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    localStorage.setItem(
      VIEW_STATE_KEY,
      JSON.stringify({ viewMode, showAll })
    );
  }, [viewMode, showAll]);

  // GET DATA -------------------------------------------
  const config = useConfig();
  useEffect(() => {
    if (!config) return;
    // get projects
    DBRequest({
      config,
      path: "projects/own?status[eq]=0",
      method: API_GET,
      onResponse: setProjects,
    });

    // get time
    DBRequest({
      config,
      path: "user/timetracking",
      method: API_GET,
      onResponse: handleTimeTrackingUserResponse,
    });
  }, [config]);

  // all projects incl. the ones the user cannot clock into (separate request, loaded on demand)
  const [allProjects, setAllProjects] = useState(null);
  useEffect(() => {
    if (!config || !showAll || allProjects) return;
    DBRequest({
      config,
      path: "projects/own?includeNonTrackable=1",
      method: API_GET,
      onResponse: setAllProjects,
    });
  }, [config, showAll, allProjects]);

  // active project --------------------------------------------
  const [projects, setProjects] = useState(null);
  const { activeProject, clockOut, clockIn, secondsIn } = useTimeTracking();

  const shownProjects = showAll ? allProjects : projects;

  // time -----------------------------------------------
  const [timeTrackingUser, setTimeTrackingUser] = useState();

  // prepare db time response data
  function handleTimeTrackingUserResponse(response, state) {
    var newData = {};

    // projects today
    newData.projectsToday = response.data.projectsToday;

    //time in Project
    newData.timeInProject = 0;

    // saving data
    setTimeTrackingUser({ ...newData });
  }

  const lastTickRef = useRef(null);

  useEffect(() => {
    if (!activeProject || !timeTrackingUser) return;

    const tick = Math.round(secondsIn / 60);

    // only update state when the rounded minute value actually changes
    if (tick === lastTickRef.current) return;
    lastTickRef.current = tick;

    setTimeTrackingUser((prev) => {
      const projectIndex = prev.projectsToday.findIndex(
        (project) => project.id === activeProject.id
      );

      const newProjectsToday = [...prev.projectsToday];

      if (projectIndex !== -1) {
        newProjectsToday[projectIndex] = {
          ...newProjectsToday[projectIndex],
          y: tick,
        };
      } else {
        newProjectsToday.push({
          x: activeProject.name,
          y: tick,
          id: activeProject.id,
        });
      }

      return {
        ...prev,
        projectsToday: newProjectsToday,
      };
    });
  }, [secondsIn, activeProject]);

  return (
    <>
      <Header title={t("Timetracking")} />
      <Box className="flex flex-col h-full overflow-y-auto">
        <TimeDashboard
          timeTrackingUser={timeTrackingUser}
          isLoading={Boolean(!timeTrackingUser)}
        />
        <Box className="flex flex-col sm:flex-row gap-2 sm:items-center mb-2">
          <SearchField
            onChange={setSearchQuery}
            autoFocus={false}
            className="grow"
          />
          <Box className="flex gap-2 items-center justify-end">
            <Tooltip
              title={t(
                "Show all projects, including those you cannot clock into"
              )}
            >
              <ToggleButton
                size="small"
                value="all"
                selected={showAll}
                onChange={() => setShowAll((prev) => !prev)}
                className="backdrop-blur-lg"
              >
                {t("Show all")}
              </ToggleButton>
            </Tooltip>
            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={(_, value) => value && setViewMode(value)}
              aria-label="View Mode"
              className="backdrop-blur-lg"
            >
              <ToggleButton value="grid" aria-label={t("Grid view")}>
                <Tooltip title={t("Grid view")}>
                  <WindowIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="list" aria-label={t("List view")}>
                <Tooltip title={t("List view")}>
                  <TableRowsIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <Box className="w-full flex flex-col relative">
          {!timeTrackingUser || !shownProjects ? (
            <Box className="flex justify-around sm:justify-start flex-wrap gap-2 h-full">
              {[...Array(10)].map((e, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={240}
                  height={373}
                  sx={{ minWidth: "240px!important" }}
                />
              ))}
            </Box>
          ) : (
            <>
              <ProjectList
                projects={shownProjects.data}
                viewMode={viewMode}
                searchQuery={searchQuery}
                showAll={showAll}
              />
              {activeProject && timeTrackingUser && (
                <Box
                  className="transition-opacity flex items-start justify-center rounded-xl absolute top-0 left-0 w-full h-full backdrop-blur-md z-10 "
                  sx={{
                    minHeight: "500px",
                    backgroundColor: colors.card,
                  }}
                >
                  <Box
                    className="flex justify-center items-center"
                    sx={{ minHeight: "500px" }}
                  >
                    <TimeAnimation className="absolute z-100" size="400px" />
                    <ProjectTimeCard
                      key={activeProject.id}
                      project={activeProject}
                      timeInProject={timeTrackingUser.timeInProject}
                      selected={true}
                      onDeselect={clockOut}
                      onSelect={clockIn}
                      backgroundColor={colors.primary[300]}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default TimeTracking;
